import { StatementRelativePeriods } from './contact';
import { StatusEnum } from './xero';
/** If policy is active, or requires approval */
export var PolicyState;
(function (PolicyState) {
    PolicyState["ACTIVE"] = "active";
    PolicyState["INACTIVE"] = "inactive";
    PolicyState["APPROVAL"] = "approval";
})(PolicyState || (PolicyState = {}));
export var PolicyType;
(function (PolicyType) {
    PolicyType["REMINDER"] = "reminder";
    PolicyType["LATE_FEE"] = "fee";
    PolicyType["DISCOUNT"] = "discount";
    PolicyType["SMS"] = "sms";
    /** E.g issued once per month per contact */
    PolicyType["STATEMENT_LATE_FEE"] = "statement_late_fee";
    PolicyType["STATEMENT"] = "statement";
    PolicyType["ESCALATION"] = "escalation";
    PolicyType["AI_CALL"] = "ai_call";
    PolicyType["QUOTE_EMAIL"] = "quote_email";
})(PolicyType || (PolicyType = {}));
export var PolicyTargetStatus;
(function (PolicyTargetStatus) {
    PolicyTargetStatus["DUE"] = "due";
    /** @todo */
    PolicyTargetStatus["MARKED_PAID"] = "marked_paid";
    /** The invoice issue date */
    PolicyTargetStatus["ISSUED"] = "issued";
    /** The expiry date when using quotes */
    PolicyTargetStatus["EXPIRY_DATE"] = "expiry_date";
})(PolicyTargetStatus || (PolicyTargetStatus = {}));
export var PolicyCalculationType;
(function (PolicyCalculationType) {
    PolicyCalculationType["FIXED"] = "fixed";
    PolicyCalculationType["PERCENT"] = "percent";
    PolicyCalculationType["PRORATA"] = "prorata";
    PolicyCalculationType["NOT_APPLICABLE"] = "not_applicable";
})(PolicyCalculationType || (PolicyCalculationType = {}));
export var PolicyProcessType;
(function (PolicyProcessType) {
    /** Add late fee as new invoice */
    PolicyProcessType["NEW_INVOICE"] = "new_invoice";
    /** Add late fee as a line item on existing */
    PolicyProcessType["UPDATE_INVOICE"] = "update_invoice";
})(PolicyProcessType || (PolicyProcessType = {}));
export var PolicyEscalationType;
(function (PolicyEscalationType) {
    PolicyEscalationType["CALL"] = "call";
    PolicyEscalationType["STOP_CREDIT"] = "stop_credit";
    PolicyEscalationType["LETTER"] = "letter";
})(PolicyEscalationType || (PolicyEscalationType = {}));
export var PolicyIntervalType;
(function (PolicyIntervalType) {
    PolicyIntervalType["MONTHLY"] = "monthly";
    PolicyIntervalType["WEEKLY"] = "weekly";
})(PolicyIntervalType || (PolicyIntervalType = {}));
export var PolicyAmountsTaxLineType;
(function (PolicyAmountsTaxLineType) {
    PolicyAmountsTaxLineType["INCLUSIVE"] = "inclusive";
    PolicyAmountsTaxLineType["EXCLUSIVE"] = "exclusive";
    PolicyAmountsTaxLineType["NO_TAX"] = "no_tax";
    PolicyAmountsTaxLineType["NOT_APPLICABLE"] = "not_applicable";
})(PolicyAmountsTaxLineType || (PolicyAmountsTaxLineType = {}));
export var PolicyStatementType;
(function (PolicyStatementType) {
    PolicyStatementType["OUTSTANDING"] = "outstanding";
    PolicyStatementType["OVERDUE"] = "overdue";
    PolicyStatementType["ALL"] = "all";
})(PolicyStatementType || (PolicyStatementType = {}));
export var PolicyGracePeriodType;
(function (PolicyGracePeriodType) {
    PolicyGracePeriodType["DAYS"] = "day";
    PolicyGracePeriodType["WEEKDAYS"] = "weekday";
})(PolicyGracePeriodType || (PolicyGracePeriodType = {}));
export var PolicyStatementInterestTo;
(function (PolicyStatementInterestTo) {
    /** When the actions runs */
    PolicyStatementInterestTo["DEFAULT"] = "default";
    /** The end of the previous month */
    PolicyStatementInterestTo["END_OF_PREVIOUS_MONTH"] = "end_of_previous_month";
})(PolicyStatementInterestTo || (PolicyStatementInterestTo = {}));
export var PolicyIncludeExcludeType;
(function (PolicyIncludeExcludeType) {
    PolicyIncludeExcludeType["INCLUDE"] = "include";
    PolicyIncludeExcludeType["EXCLUDE"] = "exclude";
})(PolicyIncludeExcludeType || (PolicyIncludeExcludeType = {}));
export const policyWeeklyDayIntervalMap = {
    1: `Monday`,
    2: `Tuesday`,
    3: `Wednesday`,
    4: `Thursday`,
    5: `Friday`,
    6: `Saturday`,
    7: `Sunday`,
};
export class Policy {
    constructor(options) {
        this.organisation_id = options.organisation_id;
        this.title = options.title || 'New Policy';
        this.deleted = false;
        this.policy_type = options.policy_type || PolicyType.LATE_FEE;
        this.minimum_threshold = options.minimum_threshold || 0;
        this.maximum_threshold = options.maximum_threshold || 0;
        this.statement_period = options.statement_period || StatementRelativePeriods.ALL_TIME;
        this.hour_of_day = options.hour_of_day || null;
        this.system = options.system || false;
        this.contact_tags = options.contact_tags || null;
        this.contact_tags_exclude_include = options.contact_tags_exclude_include || PolicyIncludeExcludeType.INCLUDE;
        this.xero_group_name = options.xero_group_name || null;
        this.xero_group_name_exclude_include = options.xero_group_name_exclude_include || PolicyIncludeExcludeType.INCLUDE;
        this.state = options.state || PolicyState.ACTIVE;
    }
}
export class LateFeePolicy extends Policy {
    constructor(options) {
        var _a, _b, _c, _d, _e;
        options.policy_type = PolicyType.LATE_FEE;
        options.title = options.title || 'New Invoice Late Fee';
        super(options);
        this.id = options.id;
        this.calculation_type = options.calculation_type || PolicyCalculationType.FIXED;
        this.process_type = options.process_type || PolicyProcessType.NEW_INVOICE;
        this.fixed_fee_amount = options.fixed_fee_amount || 1000;
        this.percent = options.percent || 5.0;
        this.grace_period = options.grace_period || 30;
        this.invoice_status = options.invoice_status || StatusEnum.AUTHORISED;
        this.repeating = options.repeating || false;
        this.repeat_frequency = options.repeat_frequency || 30;
        this.use_contact_tax_rate = options.use_contact_tax_rate || false;
        this.line_item_text = options.line_item_text || 'Late Fee Issued On #PenaltyDate';
        this.apply_after_due_date_string = options.apply_after_due_date_string || new Date().toISOString().split('T')[0];
        this.apply_before_due_date_string = options.apply_before_due_date_string || null;
        this.xero_account_code = options.xero_account_code || null;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.on_update_fail_send_new = (_a = options.on_update_fail_send_new) !== null && _a !== void 0 ? _a : true;
        this.penalty_due_date = (_b = options.penalty_due_date) !== null && _b !== void 0 ? _b : null;
        this.grace_period_type = options.grace_period_type || PolicyGracePeriodType.DAYS;
        this.exclude_account_lines = options.exclude_account_lines || null;
        this.use_central_interest_rate = (_c = options.use_central_interest_rate) !== null && _c !== void 0 ? _c : false;
        this.invoice_reference = (_d = options.invoice_reference) !== null && _d !== void 0 ? _d : `Penalty for {{{OriginalInvoiceNumber}}}`;
        this.use_invoice_number_prefix = (_e = options.use_invoice_number_prefix) !== null && _e !== void 0 ? _e : false;
        this.new_invoice_branding_theme_id = options.new_invoice_branding_theme_id || null;
        this.tracking_category_class = options.tracking_category_class || null;
        this.do_not_send_email_to_customer = options.do_not_send_email_to_customer || false;
        this.first_time_only = options.first_time_only || false;
    }
}
export class IntervalLateFeePolicy extends Policy {
    constructor(options) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        options.policy_type = PolicyType.STATEMENT_LATE_FEE;
        options.title = options.title || 'New Statement Interest';
        super(options);
        this.calculation_type = options.calculation_type === PolicyCalculationType.PRORATA ? PolicyCalculationType.PRORATA : PolicyCalculationType.PERCENT;
        this.process_type = PolicyProcessType.NEW_INVOICE;
        this.fixed_fee_amount = options.fixed_fee_amount || 0;
        this.percent = options.percent || 10.0;
        this.interval_type = options.interval_type || PolicyIntervalType.MONTHLY;
        this.day_index = options.day_index || 1;
        this.overdue_age_min = (_a = options.overdue_age_min) !== null && _a !== void 0 ? _a : 7;
        this.overdue_age_max = (_b = options.overdue_age_max) !== null && _b !== void 0 ? _b : null;
        this.amounts_tax_line_type = options.amounts_tax_line_type || PolicyAmountsTaxLineType.INCLUSIVE;
        this.invoice_status = options.invoice_status || StatusEnum.AUTHORISED;
        this.adjust_for_credit = (_c = options.adjust_for_credit) !== null && _c !== void 0 ? _c : true;
        this.statement_type = options.statement_type || PolicyStatementType.OVERDUE;
        this.penalty_due_date = (_d = options.penalty_due_date) !== null && _d !== void 0 ? _d : 0;
        this.template_id = options.template_id || null;
        this.min_fee_amount = (_e = options.min_fee_amount) !== null && _e !== void 0 ? _e : null;
        this.xero_account_code = options.xero_account_code || null;
        this.use_central_interest_rate = (_f = options.use_central_interest_rate) !== null && _f !== void 0 ? _f : false;
        this.use_invoice_number_prefix = (_g = options.use_invoice_number_prefix) !== null && _g !== void 0 ? _g : false;
        this.reference_contains = options.reference_contains || null;
        this.include_previous_interest = (_h = options.include_previous_interest) !== null && _h !== void 0 ? _h : true;
        this.new_invoice_branding_theme_id = options.new_invoice_branding_theme_id || null;
        this.statement_interest_to = options.statement_interest_to || PolicyStatementInterestTo.DEFAULT;
        this.parent_only = (_j = options.parent_only) !== null && _j !== void 0 ? _j : false;
        this.exclude_account_lines = options.exclude_account_lines || null;
        this.tracking_category_class = options.tracking_category_class || null;
    }
}
export class DiscountPolicy extends Policy {
    constructor(options) {
        options.policy_type = PolicyType.DISCOUNT;
        options.title = options.title || 'New Discount';
        super(options);
        this.calculation_type = options.calculation_type || PolicyCalculationType.FIXED;
        this.fixed_fee_amount = options.fixed_fee_amount || 1000;
        this.percent = options.percent || 5.0;
        this.grace_period = options.grace_period || 30;
        this.use_contact_tax_rate = options.use_contact_tax_rate || false;
        this.line_item_text = options.line_item_text || `Prompt payment discount if paid by #DiscountDate`;
        this.apply_after_due_date_string = options.apply_after_due_date_string || new Date().toISOString().split('T')[0];
        this.apply_before_due_date_string = options.apply_before_due_date_string || null;
        this.discount_applies = options.discount_applies || `new`;
        this.template_id = options.template_id || null;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.xero_account_code = options.xero_account_code || null;
    }
}
export class ReminderPolicy extends Policy {
    constructor(options) {
        options.policy_type = PolicyType.REMINDER;
        options.title = options.title || 'New Email';
        super(options);
        this.reminder_platform = options.reminder_platform || `email`; // TODO moved to shared for ReminderPlatformEnum.EMAIL,
        this.target_status = options.target_status || PolicyTargetStatus.DUE;
        this.day_index = options.day_index || -3;
        this.include_invoice_pdf = options.include_invoice_pdf || true;
        this.template_id = options.template_id || null;
        this.attach_ics = options.attach_ics || true;
        this.include_attachments = options.include_attachments || false;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.exclude_account_lines = options.exclude_account_lines || null;
    }
}
export class EscalationPolicy extends Policy {
    constructor(options) {
        options.policy_type = PolicyType.ESCALATION;
        options.title = options.title || 'New Escalation';
        super(options);
        this.id = options.id;
        this.apply_after_due_date_string = options.apply_after_due_date_string || new Date().toISOString().split('T')[0];
        this.apply_before_due_date_string = options.apply_before_due_date_string || null;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.escalation_type = options.escalation_type || PolicyEscalationType.CALL;
        this.escalation_emails = options.escalation_emails;
        this.day_index = options.day_index || 30;
        this.target_status = PolicyTargetStatus.DUE; // TODO - allow other triggers
        this.exclude_account_lines = options.exclude_account_lines || null;
    }
}
const defaultMessageText = `
  Hi {{{InvoiceContactName}}},
  Just a friendly reminder that your invoice {{{InvoiceNumber}}} from {{{OrganisationName}}} was due on {{{DueDate}}}.\n
`;
export class SmsPolicy extends Policy {
    constructor(options) {
        var _a, _b;
        options.policy_type = PolicyType.SMS;
        options.title = options.title || 'New SMS Alert';
        super(options);
        this.sms_content = options.sms_content || defaultMessageText;
        this.target_status = options.target_status || PolicyTargetStatus.DUE;
        this.day_index = options.day_index || 3;
        this.exclude_branding_themes = options.exclude_branding_themes || null;
        this.only_webhook = (_a = options.only_webhook) !== null && _a !== void 0 ? _a : false;
        this.sms_fallback_to_phone = (_b = options.sms_fallback_to_phone) !== null && _b !== void 0 ? _b : false;
        this.sms_default_country_code = options.sms_default_country_code || null;
    }
}
export class StatementPolicy extends Policy {
    constructor(options) {
        var _a, _b;
        options.policy_type = PolicyType.STATEMENT;
        options.title = options.title || 'New Auto Statement';
        super(options);
        this.template_id = options.template_id || null;
        this.interval_type = options.interval_type || PolicyIntervalType.MONTHLY;
        this.day_index = options.day_index || 1;
        this.statement_type = options.statement_type || PolicyStatementType.OUTSTANDING;
        this.minimum_threshold = options.minimum_threshold || 0;
        this.overdue_age_min = (_a = options.overdue_age_min) !== null && _a !== void 0 ? _a : 0;
        this.destination_email_override = options.destination_email_override || null;
        this.parent_only = (_b = options.parent_only) !== null && _b !== void 0 ? _b : false;
    }
}
export class AICallPolicy extends Policy {
    constructor(options) {
        options.policy_type = PolicyType.AI_CALL;
        options.title = options.title || 'New AI Call';
        super(options);
        this.day_index = options.day_index || 3;
        this.target_status = PolicyTargetStatus.DUE;
        this.hour_of_day = options.hour_of_day || 12;
        this.sms_default_country_code = options.sms_default_country_code || null;
    }
}
export class QuotePolicy extends Policy {
    constructor(options) {
        options.policy_type = PolicyType.QUOTE_EMAIL;
        options.title = options.title || 'New Quote Email';
        super(options);
        this.day_index = options.day_index || -3;
        this.target_status = PolicyTargetStatus.EXPIRY_DATE;
        this.hour_of_day = options.hour_of_day || 12;
    }
}
export const policyConfig = {
    apply_after_due_date_string: {
        sortIndex: 1,
        label: `Apply only to invoices due from`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS, PolicyType.ESCALATION],
    },
    apply_before_due_date_string: {
        sortIndex: 2,
        label: `Apply only to invoices due before`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS, PolicyType.ESCALATION],
    },
    minimum_threshold: {
        sortIndex: 3,
        label: `Minimum Due Amount`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS, PolicyType.STATEMENT, PolicyType.ESCALATION],
    },
    maximum_threshold: {
        sortIndex: 4,
        label: `Maximum Due Amount`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS],
    },
    exclude_branding_themes: {
        sortIndex: 5,
        label: `Exclude Branding Themes`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT, PolicyType.REMINDER, PolicyType.SMS, PolicyType.ESCALATION],
        notApplicableAccountingSystems: ['QBO'], // see below: getAccountingSystemsConfigured(ACCOUNTING_CONFIG.HIDE_INVOICE_BRANDING)
    },
    use_contact_tax_rate: {
        sortIndex: 6,
        label: `Use Contacts Tax Rate`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.DISCOUNT],
        // why does this not work...?
        // notApplicableAccountingSystems: getAccountingSystemsConfigured(ACCOUNTING_CONFIG.HIDE_CONTACT_GROUPS),
        notApplicableAccountingSystems: ['QBO'],
    },
    penalty_due_date: {
        sortIndex: 7,
        label: `Penalty Due Date`,
        advanced: false,
        appliesTo: [PolicyType.LATE_FEE],
        requiresAny: {
            process_type: PolicyProcessType.NEW_INVOICE,
            on_update_fail_send_new: true,
        },
    },
    min_fee_amount: {
        sortIndex: 8,
        label: `Minimum Fee Amount`,
        advanced: true,
        appliesTo: [PolicyType.STATEMENT_LATE_FEE],
    },
    exclude_account_lines: {
        sortIndex: 10,
        label: `Exclude invoice lines`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE, PolicyType.REMINDER, PolicyType.ESCALATION],
    },
    fixed_fee_amount: {
        sortIndex: 11,
        label: `Fixed Amount`,
        advanced: true,
        appliesTo: [PolicyType.STATEMENT_LATE_FEE],
    },
    invoice_reference: {
        sortIndex: 12,
        label: `Invoice Reference`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE],
        span: `full`,
        requiresAny: {
            process_type: PolicyProcessType.NEW_INVOICE,
            on_update_fail_send_new: true,
        },
    },
    hour_of_day: {
        sortIndex: 13,
        label: `Action Time`,
        appliesTo: [
            PolicyType.STATEMENT_LATE_FEE,
            PolicyType.STATEMENT,
            PolicyType.REMINDER,
            PolicyType.SMS,
            PolicyType.ESCALATION,
            PolicyType.LATE_FEE,
            PolicyType.AI_CALL,
            // PolicyType.DISCOUNT,
        ],
        advanced: [
            PolicyType.STATEMENT_LATE_FEE,
            PolicyType.STATEMENT,
            PolicyType.REMINDER,
            PolicyType.SMS,
            PolicyType.ESCALATION,
            PolicyType.LATE_FEE,
        ],
    },
    use_invoice_number_prefix: {
        sortIndex: 14,
        label: `Use custom invoice numbers`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE],
        notApplicableAccountingSystems: [`XERO`],
        requiresAny: {
            process_type: PolicyProcessType.NEW_INVOICE,
            on_update_fail_send_new: true,
        },
    },
    reference_contains: {
        sortIndex: 15,
        label: `Include Invoices with Reference`,
        advanced: true,
        notApplicableAccountingSystems: [`QBO`],
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE, PolicyType.REMINDER, PolicyType.ESCALATION],
    },
    only_webhook: {
        sortIndex: 16,
        label: `Only trigger webhook`,
        advanced: true,
        span: `full`,
        appliesTo: [PolicyType.SMS],
        requiresFeature: `webhooks`,
    },
    destination_email_override: {
        sortIndex: 17,
        label: `Override Destination Email`,
        advanced: true,
        appliesTo: [PolicyType.STATEMENT],
    },
    new_invoice_branding_theme_id: {
        sortIndex: 18,
        label: `Use Branding Theme`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE],
        notApplicableAccountingSystems: ['QBO'],
    },
    parent_only: {
        sortIndex: 19,
        span: `full`,
        label: `Only apply to parent customers`,
        advanced: true,
        appliesTo: [PolicyType.STATEMENT, PolicyType.STATEMENT_LATE_FEE],
        notApplicableAccountingSystems: ['XERO'],
    },
    sms_default_country_code: {
        sortIndex: 20,
        label: `Default country code`,
        advanced: [PolicyType.SMS],
        appliesTo: [PolicyType.SMS, PolicyType.AI_CALL],
    },
    sms_fallback_to_phone: {
        sortIndex: 21,
        label: `Use business phone number`,
        advanced: true,
        appliesTo: [PolicyType.SMS],
    },
    tracking_category_class: {
        sortIndex: 22,
        label: `Use Tracking Category or Class`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE],
    },
    contact_tags: {
        sortIndex: 22,
        label: `Contact Tags`,
        advanced: true,
        span: `full`,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.AI_CALL, PolicyType.SMS, PolicyType.REMINDER, PolicyType.ESCALATION, PolicyType.DISCOUNT, PolicyType.STATEMENT, PolicyType.STATEMENT_LATE_FEE],
    },
    xero_group_name: {
        sortIndex: 24,
        label: `Xero Group Name`,
        advanced: true,
        span: `full`,
        appliesTo: [PolicyType.LATE_FEE, PolicyType.AI_CALL, PolicyType.SMS, PolicyType.REMINDER, PolicyType.ESCALATION, PolicyType.DISCOUNT, PolicyType.STATEMENT, PolicyType.STATEMENT_LATE_FEE],
        notApplicableAccountingSystems: ['QBO'],
    },
    do_not_send_email_to_customer: {
        sortIndex: 25,
        label: `Do not send email to customer`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE],
    },
    first_time_only: {
        sortIndex: 26,
        label: `First time only`,
        advanced: true,
        appliesTo: [PolicyType.LATE_FEE],
    },
};
export const statementTypes = [
    PolicyType.STATEMENT_LATE_FEE,
    PolicyType.STATEMENT,
];
export const quoteTypes = [
    PolicyType.QUOTE_EMAIL,
];
export const invoiceTypes = [
    PolicyType.LATE_FEE,
    PolicyType.DISCOUNT,
    PolicyType.REMINDER,
    PolicyType.SMS,
    PolicyType.ESCALATION,
    PolicyType.AI_CALL,
];
// @ash - a different approach to the above, the above starts at the field level, this starts at the policy level
export const policyConfigV2 = {
    [PolicyType.LATE_FEE]: {
        previewConfig: {
            canPreview: true,
            skipFields: [
                `maximum_threshold`,
                `minimum_threshold`,
                `apply_after_due_date_string`,
                `apply_before_due_date_string`,
                `grace_period`,
            ],
        },
    },
    [PolicyType.DISCOUNT]: {
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.REMINDER]: {
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.SMS]: {
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.STATEMENT_LATE_FEE]: {
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.STATEMENT]: {
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.ESCALATION]: {
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.AI_CALL]: {
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
    [PolicyType.QUOTE_EMAIL]: {
        previewConfig: {
            canPreview: false,
            skipFields: [],
        },
    },
};
//# sourceMappingURL=policy.js.map